<template>
  <ModalSmall :title="title" @close="close" :is-visible-button-close>
    <!-- 언어선택 -->
    <template v-if="recordState === State.LANGUAGE">
      <div class="container-addvoice">
        <h4>{{ t('apec_language_mine') }}</h4>
        <ApecDropdownLanguage
          v-model:language-selected="languageSelected"
          :is-visible-error-language="isVisibleErrorLanguage"
        />
        <ButtonProcess full-width @click="handleSelectLanguage">
          {{ t('confirm') }}
        </ButtonProcess>
      </div>
    </template>

    <!-- ERROR or RECORDED 상태 -->
    <template v-else-if="[State.RECORDED, State.ERROR].includes(recordState)">
      <div class="container-addvoice">
        <!-- RECORDED 상태 -->
        <div v-if="recordState === State.RECORDED" class="container-addvoice">
          <CheckAnimation :width="64" :height="64" class="check-animation" />
          <h4>{{ t('entered_register_voice') }}</h4>
          <p>{{ t('guide_voice_register_info_3') }}</p>
          <ButtonProcess class="full-width" @click="handleAddVoiceClick" :loading="uploading">
            {{ t('btn_register_my_voice') }}
          </ButtonProcess>
        </div>

        <!-- ERROR 상태 -->
        <div v-else-if="recordState === State.ERROR" class="container-addvoice error">
          <p>{{ errorMessage }} {{ errorMessage2 }}</p>
          <ButtonProcess full-width @click="handleAddVoiceClick">
            {{ t('guide_voice_register_btn_retry') }}
          </ButtonProcess>
        </div>
      </div>
    </template>

    <template v-else>
      <!-- BEFORE 상태 -->
      <div class="container-addvoice">
        <div class="wrapper-guide" ref="wrapperGuideRef" :style="{ minHeight: wrapperGuideMinHeight + 'px' }">
          <p v-if="recordState === State.RECORDING">
            <i18n-t keypath="finish_register_my_voice" scope="global">
              <template #register>
                <span class="highlight-text">{{ t('button_voice_register') }}</span>
              </template>
            </i18n-t>
          </p>
          <p v-else>
            <i18n-t keypath="do_register_my_voice" scope="global">
              <template #start>
                <span class="highlight-text">{{ t('button_start') }}</span>
              </template>
            </i18n-t>
          </p>
        </div>
        <p v-if="route.params.code === 'xCCDJ'">{{ t('for_webstore') }}</p>
        <div class="example-word-container">
          <div class="example-word-wrapper" ref="exampleWordRef" :style="{ minHeight: exampleWordMinHeight + 'px' }">
            <p v-if="showMicPreparing" class="light">
              {{ t('preparing_mic') }}
            </p>
            <h4 class="semibold" v-else-if="longVoice">
              {{ localT('example_word_1') }}
              {{ localT('example_word_2') }}
            </h4>
            <h4 class="semibold" v-else>{{ localT('example_word_short') }}</h4>
          </div>
        </div>
        <ButtonProcess
          full-width
          v-if="recordState === State.BEFORE && !micLoading"
          @click="handleAddVoiceClick"
          :disabled="micLoading"
        >
          {{ t('button_start') }}
        </ButtonProcess>
        <div class="link-wrap" v-if="recordState === State.BEFORE && !micLoading">
          <a class="small-link-light" @click="close">{{ t('skip') }}</a>
        </div>
      </div>

      <!-- RECORDING 상태 -->
      <div v-if="recordState === State.RECORDING" class="container-addvoice">
        <div class="record-wrap">
          <div
            class="record-circle-dot"
            :class="{
              preparing: showMicPreparing,
              ready: !showMicPreparing,
            }"
          ></div>
          <div
            class="record-circle"
            :style="{
              width: microphoneStore.outerRadius * 1.25 + 'rem',
              height: microphoneStore.outerRadius * 1.25 + 'rem',
            }"
          ></div>
        </div>
        <div class="button-wrapper">
          <ButtonProcess secondary class="full-width" @click="readAgain">
            {{ t('read_again') }}
          </ButtonProcess>
          <ButtonProcess full-width @click="handleAddVoiceClick" :loading="uploading" :disabled="showMicPreparing">
            {{ t('button_voice_register') }}
          </ButtonProcess>
        </div>
        <div class="link-wrap">
          <a class="small-link-light" @click="close">{{ t('skip') }}</a>
        </div>
      </div>
    </template>
  </ModalSmall>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { storeToRefs } from 'pinia';
import { WaveFile } from 'wavefile';

import { registerVoice } from '@/api/apiService';
import ApecDropdownLanguage from '@/components/domains/apec/common/ApecDropdownLanguage.vue';
import CheckAnimation from '@/components/icons/IconCheckAnimation.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import ModalSmall from '@/components/shared/composite/ModalSmall.vue';
import transformedMessages from '@/static/messageBylanguages';
import { useAudioStore } from '@/stores/audio';
import { useMicrophoneStore } from '@/stores/microphone.js';
import { useSocketStore } from '@/stores/socket';
import { useToastStore } from '@/stores/toast.js';
import { clippingVoice } from '@/utils/audioGainController.js';
import { SAMPLE_RATE } from '@/utils/constants/microphone';

const props = withDefaults(
  defineProps<{
    isUsingSelectLanguage?: boolean;
    device?: any;
    longVoice?: boolean;
  }>(),
  {
    isUsingSelectLanguage: false,
    device: () => ({}),
    longVoice: false,
  }
);

const emit = defineEmits<{
  close: [isSuccess: boolean];
  'voice-registered': [];
}>();

enum State {
  LANGUAGE = 'LANGUAGE',
  BEFORE = 'BEFORE',
  RECORDING = 'RECORDING',
  CHECKING = 'CHECKING',
  RECORDED = 'RECORDED',
  ERROR = 'ERROR',
}

const toastStore = useToastStore();
const socketStore = useSocketStore();
const microphoneStore = useMicrophoneStore();
const audioStore = useAudioStore();
const { languageSelected } = storeToRefs(audioStore);
const { micSensitivity } = storeToRefs(microphoneStore);

const route = useRoute();
const { t } = useI18n();
const { t: localT, locale } = useI18n({
  messages: transformedMessages,
  useScope: 'local',
});

const recordState = ref<State>(props.isUsingSelectLanguage ? State.LANGUAGE : State.BEFORE);

const uploading = ref<boolean>(false);

const micLoading = ref<boolean>(false);
const isClickThrottled = ref<boolean>(false);
const showMicPreparing = ref<boolean>(false);

const title = ref(props.isUsingSelectLanguage ? t('apec_language_choose') : t('register_my_voice'));
const errorMessage = ref<string>('');
const errorMessage2 = ref<string>('');

const exampleWordRef = ref(null);
const exampleWordMinHeight = ref<number>(0);

const wrapperGuideRef = ref(null);
const wrapperGuideMinHeight = ref(0);

const voiceRecordingResult = ref({});
const audioBufferMap = ref([]);

const isVisibleErrorLanguage = ref<boolean>(false);

// 모달을 닫을 수 있는지
const isVisibleButtonClose = computed(() => {
  const isDm = location.pathname === '/apec/chat/dm';
  const isConference = location.pathname === '/apec/conference';
  const isStateLanguage = recordState.value === State.LANGUAGE;
  // DM, 컨퍼런스 모드이며 언어선택 단계일 경우 모달 닫을 수 없음
  return !(isStateLanguage && (isDm || isConference));
});

const done = async () => {
  uploading.value = true;
  await handleVoiceRecordingResult();
  errorMessage.value = '';
  errorMessage2.value = '';
  const languageCode = audioStore.languageSelected?.code;
  const deviceLabel = props.device?.label;
  const reference = props.longVoice
    ? `${localT('example_word_1')} ${localT('example_word_2')}`
    : localT('example_word_short');

  const result = await registerVoice(voiceRecordingResult.value, deviceLabel, languageCode, reference);
  if (result && ![1021, 1030, 1031].includes(result)) {
    toastStore.info(t('succeed_register_voice'));
    emit('close', result);
    emit('voice-registered');
  } else {
    title.value = t('guide_voice_error');
    errorMessage.value =
      {
        1021: t('guide_voice_register_too_short_1'),
        1030: t('guide_voice_register_too_short_1'),
        1031: t('guide_voice_register_invalid_1'),
      }[result] || t('guide_voice_register_upload_error_1');
    errorMessage2.value =
      {
        1021: t('guide_voice_register_too_short_2'),
        1030: t('guide_voice_register_too_short_2'),
        1031: t('guide_voice_register_invalid_2'),
      }[result] || t('guide_voice_register_upload_error_2');

    recordState.value = State.ERROR;
    uploading.value = false;
  }
};
const handleSelectLanguage = () => {
  if (!languageSelected.value) {
    isVisibleErrorLanguage.value = true;
    return;
  }
  socketStore.sendMessageChangeLanguage(languageSelected.value.code);
  locale.value = languageSelected.value.code;
  recordState.value = State.BEFORE;
};

const handleAddVoiceClick = async () => {
  if (isClickThrottled.value) return;

  isClickThrottled.value = true;

  errorMessage.value = '';
  errorMessage2.value = '';

  if (recordState.value === State.BEFORE || recordState.value === State.ERROR) {
    showMicPreparing.value = true;
    micLoading.value = true;
    title.value = t('register_my_voice');
    recordState.value = State.RECORDING;

    try {
      if (clippingVoice.value) {
        micSensitivity.value = micSensitivity.value * 0.5;
        microphoneStore.controlMicGain();
        clippingVoice.value = false;
      }
      await microphoneStore.startMic(props.device.id);
    } catch (e) {
      console.error(e);
    } finally {
      micLoading.value = false;
      showMicPreparing.value = false;
    }
  } else if (recordState.value === State.RECORDING) {
    uploading.value = true;
    await microphoneStore.clearMic();
    await done();
  }

  isClickThrottled.value = false;
};

const readAgain = async () => {
  await microphoneStore.clearMic();
  recordState.value = State.BEFORE;
};

const handleVoiceRecordingResult = async () => {
  const wav = new WaveFile();
  wav.fromScratch(1, SAMPLE_RATE, '32f', [...audioBufferMap.value]);

  const wavBuffer = wav.toBuffer();
  const wavBlob = new Blob([wavBuffer], { type: 'audio/wav' });

  voiceRecordingResult.value = {
    wavBlob,
    sampleRate: SAMPLE_RATE,
    durationSec: 5,
  };

  audioBufferMap.value = [];
};

const close = async () => {
  await microphoneStore.initMicrophoneStore();
  emit('close');
};

watch(
  () => voiceRecordingResult.value,
  (recordedAudio) => {
    if (recordedAudio && recordState.value === State.CHECKING) {
      recordState.value = State.CHECKING;
    }
  }
);

watch(
  () => props.longVoice, // or any condition that triggers height change
  async () => {
    await nextTick();
    if (exampleWordRef.value) {
      const currentHeight = exampleWordRef.value.offsetHeight;
      if (currentHeight > exampleWordMinHeight.value) {
        exampleWordMinHeight.value = currentHeight;
      }
    }
  },
  { immediate: true }
);

watch(
  () => recordState.value,
  async () => {
    await nextTick();
    if (wrapperGuideRef.value) {
      const currentHeight = wrapperGuideRef.value.offsetHeight;
      if (currentHeight > wrapperGuideMinHeight.value) {
        wrapperGuideMinHeight.value = currentHeight;
      }
    }
  }
);

onMounted(async () => {
  await nextTick();
  locale.value = languageSelected.value ? languageSelected.value.code : 'en_US';
  if (exampleWordRef.value) {
    exampleWordMinHeight.value = exampleWordRef.value.offsetHeight;
  }
  await microphoneStore.initMicrophoneStore({
    isUsingToast: true,
    isUsingSpecificGain: true,
  });
  microphoneStore.initMicEvent((data: MessageEvent<Float32Array>) => {
    audioBufferMap.value.push(...data);
  });
  if (wrapperGuideRef.value) {
    wrapperGuideMinHeight.value = wrapperGuideRef.value.offsetHeight;
  }
});
</script>

<style scoped lang="scss">
.container-addvoice {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.5rem;
  white-space: pre-line;
}

.error {
  gap: 1rem;
}

.wrapper-guide {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.dot {
  width: 0.25rem;
  height: 0.25rem;
  margin: 1rem auto;
  border-radius: 50%;
  background-color: var(--color-navy-50);
}

.example-word-container {
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 0.5rem;
  margin: 0.5rem 0;
  padding: 1rem 2rem;
  justify-content: space-between;
  background-color: var(--background-addmyvoice-text);
  border-radius: 8px;
  font-size: var(--font-size-p);
  font-weight: 400;
  color: var(--color-gray-900);
  word-break: break-word;
}

.example-word-wrapper {
  min-height: 4.5rem;
  align-content: center;
}

.icon-info {
  width: 1rem;
  height: 1rem;
  fill: var(--color-gray-100);
  margin-top: 0.0625rem;
}

.info-wrap {
  display: flex;
  flex-direction: row;
  color: var(--color-gray-100);
  gap: 0.25rem;
}

.button-wrapper {
  width: 100%;
  height: 3rem;
  display: flex;
  gap: 0.5rem;
}

.link-wrap {
  height: fit-content;
  align-content: center;
  text-align: center;
  margin-top: 0.5rem;
}

.button-record-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  margin-top: 0.5rem;
  border-radius: 8px;
  background-color: var(--color-navy-10);
  align-items: center;
  text-align: center;
}

.button-record {
  position: relative;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  outline: solid 2px var(--color-navy-20);
  background-color: var(--color-white);
}

.button-record:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  animation: fadeOut 0.5s ease-out;
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0.5;
  }
}

.button-record-circle {
  display: flex;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 75%;
  height: 75%;
  border-radius: 50%;
  background-color: var(--color-red);
}

.record-wrap {
  position: relative;
  width: 3rem;
  height: 3rem;
  margin: 0 auto 0.5rem auto;
  border-radius: 50%;
  background-color: var(--background-dot);
}

.record-circle {
  position: absolute;
  top: 50%;
  left: 50%;
  background: var(--dot);
  opacity: 0.2;
  border-radius: 50%;
  width: 100%;
  height: 100%;
  animation: pulse 1s infinite;
  transform: translate(-50%, -50%);
}

.record-circle-dot {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0.75rem;
  height: 0.75rem;
  background: var(--dot);
  border-radius: 50%;
  transform: translate(-50%, -50%);
}

.record-circle-dot.preparing {
  opacity: 0.2;
}

.record-circle-dot.ready {
  animation: fadeIn 0.5s ease-in forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.record-button-wrapper {
  margin-top: 0.5rem;
}

.record-button-wrapper div {
  width: 100%;
  display: flex;
  justify-content: center;
  background-color: #fff;
}

.record-button-wrapper div img {
  width: 40px;
  cursor: pointer;
  height: 40px;
  transition: all 0.2s ease-in-out;
}

.record-button-wrapper-btn {
  width: 40px;
  cursor: pointer;
  height: 40px;
  padding: 6px;
  border-radius: 20px;
  background: var(--Profile-Navy-Navy-15-bg-primary-dark, #e7effc);
}

.record-button-wrapper-btn:hover {
  transform: scale(1.1);
}

.record-button-wrapper div img:hover {
  transform: scale(1.1);
}

.add-voice-success-wrap {
  width: 100%;
  justify-items: center;
  align-items: center;
}

.add-voice-success-wrap > img {
  width: 40px;
  height: 40px;
}

.add-voice-success-wrap > button {
  width: 100%;
  background-color: var(--color-navy-100);
  color: var(--color-white);
  border-radius: 8px;
  padding: 12px 32px;
  margin-top: 16px;
  font-size: medium;
  line-height: 1.5rem;
}

.check-animation {
  margin: 0 auto;
}

.uploading-text {
  margin-top: 0.5rem;
  color: var(--color-gray-100);
}

.uploading-text2 {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  color: var(--color-gray-40);
}

.add-voice-success-wrap button.disabled {
  opacity: 0.3;
  background: var(--color-navy-100);
  cursor: not-allowed;
}

.step-indicator-wrap {
  margin-top: 1rem;
}
</style>

<style>
.highlight-text {
  background-color: var(--color-navy-100);
  padding: 0.25rem 0.375rem;
  color: var(--color-white);
  font-weight: var(--font-weight-semibold);
  border-radius: 4px;
}
</style>
